/**
 * In-game pause menu. Toggled by the GameDirector on ESC. Three options:
 *
 *   Continue   — close the menu and resume play (also fires when the
 *                player presses ESC again or clicks the backdrop)
 *   Restart    — hard reload of the page back to the intro flow
 *   Save Game  — placeholder for now; persistence is not wired up
 *
 * The visual identity matches the rest of the in-game overlays (deep
 * navy panel, --faction blue glow, --hud-accent amber for primary CTAs).
 */

export interface PauseMenuCallbacks {
  readonly onContinue: () => void;
  readonly onRestart: () => void;
  readonly onSave: () => void;
}

export class PauseMenuOverlay {
  readonly element: HTMLElement;
  private visible = false;
  private readonly continueBtn: HTMLButtonElement;
  private callbacks: PauseMenuCallbacks;

  constructor(callbacks: PauseMenuCallbacks) {
    this.callbacks = callbacks;
    this.element = document.createElement('div');
    this.element.className = 'pause-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'pause-backdrop';
    // Click on the dimmed area resumes play — same as ESC.
    backdrop.addEventListener('click', () => this.callbacks.onContinue());

    const panel = document.createElement('section');
    panel.className = 'pause-panel';
    panel.addEventListener('click', (ev) => ev.stopPropagation());

    const eyebrow = document.createElement('div');
    eyebrow.className = 'pause-eyebrow';
    eyebrow.textContent = 'PAUSED';

    const title = document.createElement('h2');
    title.className = 'pause-title';
    title.textContent = 'Engines Idle';

    const subtitle = document.createElement('p');
    subtitle.className = 'pause-subtitle';
    subtitle.textContent =
      'Take a breath. Resume when ready, or restart the run from scratch.';

    const list = document.createElement('div');
    list.className = 'pause-actions';

    this.continueBtn = this.buildAction({
      label: 'Continue',
      hint: 'Resume the run · TAB',
      icon: PLAY_ICON,
      primary: true,
      onClick: () => this.callbacks.onContinue()
    });
    list.appendChild(this.continueBtn);

    list.appendChild(
      this.buildAction({
        label: 'Restart',
        hint: 'Reload from the title screen',
        icon: RESTART_ICON,
        primary: false,
        onClick: () => this.callbacks.onRestart()
      })
    );

    list.appendChild(
      this.buildAction({
        label: 'Save Game',
        hint: 'Download a .txt save file',
        icon: SAVE_ICON,
        primary: false,
        onClick: () => this.callbacks.onSave()
      })
    );

    const footer = document.createElement('footer');
    footer.className = 'pause-footer';
    footer.textContent = 'Press TAB to resume';

    panel.append(eyebrow, title, subtitle, list, footer);
    this.element.append(backdrop, panel);
  }

  isOpen(): boolean {
    return this.visible;
  }

  /** Replace any of the callback handlers post-construction — used by
   *  GameDirector to wire the Continue button into its resume logic
   *  after both pieces have been instantiated. */
  setOnContinue(handler: () => void): void {
    this.callbacks = { ...this.callbacks, onContinue: handler };
  }

  setOnRestart(handler: () => void): void {
    this.callbacks = { ...this.callbacks, onRestart: handler };
  }

  setOnSave(handler: () => void): void {
    this.callbacks = { ...this.callbacks, onSave: handler };
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.element.classList.add('is-visible');
    // Focus continue so Enter immediately resumes — small QoL.
    requestAnimationFrame(() => this.continueBtn.focus({ preventScroll: true }));
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('is-visible');
    if (
      document.activeElement &&
      typeof (document.activeElement as HTMLElement).blur === 'function'
    ) {
      (document.activeElement as HTMLElement).blur();
    }
  }

  private buildAction(opts: {
    label: string;
    hint: string;
    icon: string;
    primary: boolean;
    disabled?: boolean;
    onClick: () => void;
  }): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pause-action';
    if (opts.primary) btn.classList.add('is-primary');
    if (opts.disabled) {
      btn.classList.add('is-disabled');
      btn.disabled = true;
    }
    btn.innerHTML = `
      <span class="pause-action-icon" aria-hidden="true">${opts.icon}</span>
      <span class="pause-action-body">
        <span class="pause-action-label">${opts.label}</span>
        <span class="pause-action-hint">${opts.hint}</span>
      </span>
      <span class="pause-action-arrow" aria-hidden="true">→</span>
    `;
    btn.addEventListener('click', () => {
      if (opts.disabled) return;
      opts.onClick();
    });
    return btn;
  }
}

const PLAY_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M7 4.5v15l13-7.5L7 4.5z" fill="currentColor" />
</svg>`;

const RESTART_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M5 12a7 7 0 0 1 12-4.95" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <path d="M19 12a7 7 0 0 1-12 4.95" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <path d="M16 4v3.5h3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
  <path d="M8 20v-3.5H4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

const SAVE_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
  <path d="M8 4v5h7V4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <path d="M8 14h8v6H8z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
</svg>`;
